import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCcw, Gauge, Info, Sun, Moon } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Slider } from '../../../components/ui/slider';
import { Badge } from '../../../components/ui/badge';
import { SCIENCE_THEME_KEY } from '../Class9SciencePage';

/**
 * Class 9 NCERT Science · Chapter 5 — Motion
 * Interactive simulator: a car moves along a track based on user-controlled acceleration.
 * Live distance-time + velocity-time graphs draw alongside.
 */

const TRACK_W = 720;
const TRACK_H = 140;
const GRAPH_W = 360;
const GRAPH_H = 170;
const T_MAX = 20; // seconds visible on graph
const D_MAX = 200; // metres visible on graph
const V_MAX = 30; // m/s visible

const MotionSimulation = () => {
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const dtGraphRef = useRef(null);
  const vtGraphRef = useRef(null);
  const animRef = useRef(null);

  const [acceleration, setAcceleration] = useState(2); // m/s²
  const [running, setRunning] = useState(true);
  const stateRef = useRef({ t: 0, v: 0, d: 0, samples: [], lastTs: null });
  const [display, setDisplay] = useState({ t: 0, v: 0, d: 0 });
  const [theme, setTheme] = useState(() => localStorage.getItem(SCIENCE_THEME_KEY) || 'light');
  useEffect(() => { localStorage.setItem(SCIENCE_THEME_KEY, theme); }, [theme]);
  const isLight = theme === 'light';
  const cls = useMemo(() => ({
    page:    isLight ? 'bg-gray-50 text-gray-900' : 'bg-[#0A0F1C] text-white',
    topbar:  isLight ? 'border-gray-200 bg-white' : 'border-white/5',
    card:    isLight ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#0F1626] border-white/10',
    mutedText: isLight ? 'text-gray-500' : 'text-gray-500',
    softText:  isLight ? 'text-gray-700' : 'text-gray-400',
    label:     isLight ? 'text-gray-400' : 'text-gray-600',
    backBtn:   isLight ? 'text-gray-600 hover:text-teal-600' : 'text-gray-400 hover:text-teal-400',
    btnOutline: isLight ? 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700' : 'border-white/10 bg-white/[0.02] hover:bg-white/5',
  }), [isLight]);

  const reset = () => {
    stateRef.current = { t: 0, v: 0, d: 0, samples: [], lastTs: null };
    setDisplay({ t: 0, v: 0, d: 0 });
  };

  useEffect(() => {
    if (!running) return;
    const drawTrack = (ctx, d, v) => {
      ctx.clearRect(0, 0, TRACK_W, TRACK_H);
      // Sky gradient
      const sky = ctx.createLinearGradient(0, 0, 0, TRACK_H);
      sky.addColorStop(0, '#0F1626');
      sky.addColorStop(1, '#1a2438');
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, TRACK_W, TRACK_H);
      // Road
      ctx.fillStyle = '#1f2937';
      ctx.fillRect(0, TRACK_H - 50, TRACK_W, 50);
      // Dashed centre line
      ctx.strokeStyle = '#facc15';
      ctx.lineWidth = 2;
      ctx.setLineDash([18, 14]);
      const offset = (d * 4) % 32;
      ctx.beginPath();
      ctx.moveTo(-offset, TRACK_H - 25);
      ctx.lineTo(TRACK_W, TRACK_H - 25);
      ctx.stroke();
      ctx.setLineDash([]);

      // Distance markers (every 50m)
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.font = '10px monospace';
      const startMark = Math.floor(d / 50) * 50;
      for (let m = startMark; m < startMark + 250; m += 50) {
        const px = ((m - d) * 4) + 80;
        if (px >= 0 && px <= TRACK_W) {
          ctx.beginPath();
          ctx.moveTo(px, TRACK_H - 50);
          ctx.lineTo(px, TRACK_H - 45);
          ctx.stroke();
          ctx.fillText(`${m}m`, px - 8, TRACK_H - 55);
        }
      }

      // Car (fixed at x=80, world moves)
      const carX = 80;
      const carY = TRACK_H - 78;
      // Body
      ctx.fillStyle = '#14b8a6';
      ctx.fillRect(carX, carY + 8, 60, 16);
      // Roof
      ctx.fillStyle = '#0ea5e9';
      ctx.beginPath();
      ctx.moveTo(carX + 10, carY + 8);
      ctx.lineTo(carX + 18, carY - 2);
      ctx.lineTo(carX + 50, carY - 2);
      ctx.lineTo(carX + 58, carY + 8);
      ctx.closePath();
      ctx.fill();
      // Wheels
      ctx.fillStyle = '#111827';
      ctx.beginPath(); ctx.arc(carX + 15, carY + 26, 6, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(carX + 47, carY + 26, 6, 0, Math.PI * 2); ctx.fill();
      // Wheel rims
      ctx.strokeStyle = '#9ca3af';
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(carX + 15, carY + 26, 6, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(carX + 47, carY + 26, 6, 0, Math.PI * 2); ctx.stroke();
      // Speed lines if moving fast
      if (v > 5) {
        ctx.strokeStyle = `rgba(20, 184, 166, ${Math.min(0.8, v / 20)})`;
        ctx.lineWidth = 1.5;
        for (let i = 0; i < 4; i++) {
          ctx.beginPath();
          ctx.moveTo(carX - 5 - i * 10, carY + 4 + i * 5);
          ctx.lineTo(carX - 20 - i * 10, carY + 4 + i * 5);
          ctx.stroke();
        }
      }
    };

    const drawGraph = (ctx, title, samples, yKey, yMax, yLabel, color) => {
      ctx.clearRect(0, 0, GRAPH_W, GRAPH_H);
      // Background
      ctx.fillStyle = '#0F1626';
      ctx.fillRect(0, 0, GRAPH_W, GRAPH_H);
      // Title
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = 'bold 11px sans-serif';
      ctx.fillText(title, 10, 16);
      // Plot area
      const PADL = 36, PADR = 10, PADT = 26, PADB = 22;
      const pw = GRAPH_W - PADL - PADR;
      const ph = GRAPH_H - PADT - PADB;
      // Axes
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 1;
      // Y grid
      for (let i = 0; i <= 4; i++) {
        const y = PADT + (ph / 4) * i;
        ctx.beginPath();
        ctx.moveTo(PADL, y);
        ctx.lineTo(GRAPH_W - PADR, y);
        ctx.stroke();
      }
      // X grid
      for (let i = 0; i <= 4; i++) {
        const x = PADL + (pw / 4) * i;
        ctx.beginPath();
        ctx.moveTo(x, PADT);
        ctx.lineTo(x, PADT + ph);
        ctx.stroke();
      }
      // Axis labels
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '9px monospace';
      // Y labels
      for (let i = 0; i <= 4; i++) {
        const val = Math.round(yMax - (yMax / 4) * i);
        ctx.fillText(`${val}`, 6, PADT + (ph / 4) * i + 3);
      }
      // X labels (time)
      for (let i = 0; i <= 4; i++) {
        const t = (T_MAX / 4) * i;
        ctx.fillText(`${t}`, PADL + (pw / 4) * i - 4, GRAPH_H - 6);
      }
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.font = '9px sans-serif';
      ctx.fillText(yLabel, 6, 18);
      ctx.fillText('t (s)', GRAPH_W - 28, GRAPH_H - 6);

      // Plot line
      if (samples.length > 1) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        samples.forEach((s, i) => {
          const x = PADL + (Math.min(s.t, T_MAX) / T_MAX) * pw;
          const yv = Math.min(s[yKey], yMax);
          const y = PADT + ph - (yv / yMax) * ph;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
        // Marker on latest point
        const last = samples[samples.length - 1];
        const lx = PADL + (Math.min(last.t, T_MAX) / T_MAX) * pw;
        const ly = PADT + ph - (Math.min(last[yKey], yMax) / yMax) * ph;
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(lx, ly, 3.5, 0, Math.PI * 2); ctx.fill();
      }
    };

    const loop = (ts) => {
      const s = stateRef.current;
      if (s.lastTs === null) s.lastTs = ts;
      const dt = Math.min(0.1, (ts - s.lastTs) / 1000);
      s.lastTs = ts;

      // Update physics: v = u + at, d = d + v*dt
      s.v = Math.max(0, s.v + acceleration * dt);
      s.d += s.v * dt;
      s.t += dt;

      // Sample (limit to ~80 points)
      if (s.samples.length === 0 || s.t - s.samples[s.samples.length - 1].t > 0.1) {
        s.samples.push({ t: s.t, v: s.v, d: s.d });
        if (s.samples.length > 200) s.samples.shift();
      }

      // Reset when t exceeds graph window
      if (s.t > T_MAX || s.d > D_MAX * 5) {
        s.t = 0; s.v = 0; s.d = 0; s.samples = [];
      }

      // Draw
      if (trackRef.current) drawTrack(trackRef.current.getContext('2d'), s.d, s.v);
      if (dtGraphRef.current) drawGraph(dtGraphRef.current.getContext('2d'), 'Distance vs Time', s.samples, 'd', D_MAX, 'd (m)', '#14b8a6');
      if (vtGraphRef.current) drawGraph(vtGraphRef.current.getContext('2d'), 'Velocity vs Time', s.samples, 'v', V_MAX, 'v (m/s)', '#f59e0b');

      setDisplay({ t: s.t, v: s.v, d: s.d });

      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animRef.current);
  }, [running, acceleration]);

  const motionType = acceleration === 0
    ? 'Uniform Motion'
    : acceleration > 0
      ? 'Uniformly Accelerated'
      : 'Uniformly Decelerated';

  return (
    <div className={`min-h-screen ${cls.page}`} data-testid="sim-motion">
      <div className={`border-b ${cls.topbar} px-6 py-4 flex items-center justify-between`}>
        <button onClick={() => navigate('/science/class-9')} className={`inline-flex items-center gap-2 text-sm ${cls.backBtn}`}>
          <ArrowLeft className="w-4 h-4" /> Back to Class 9 Chapters
        </button>
        <div className="flex items-center gap-3">
          <Badge className={`${isLight ? 'bg-cyan-100 text-cyan-700 border-cyan-200' : 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30'} text-xs`}>CBSE · Class 9 · Chapter 5</Badge>
          <button
            onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
            className={`p-2 rounded-lg ${isLight ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-white/5 text-gray-400'} transition-colors`}
            title={`Switch to ${isLight ? 'dark' : 'light'} mode`}
            data-testid="theme-toggle"
          >
            {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold mb-2">Motion</h1>
        <p className={`text-sm ${cls.softText} mb-8 max-w-2xl`}>
          Control the acceleration. Watch the car move along the track while distance-time and velocity-time graphs draw in real time.
        </p>

        {/* Live readouts */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className={`${cls.card} border rounded-xl p-4`}>
            <p className={`text-[11px] ${cls.mutedText} uppercase tracking-wider mb-1`}>Time (t)</p>
            <p className="text-2xl font-bold font-mono">{display.t.toFixed(2)}<span className={`text-sm ${cls.mutedText} ml-1`}>s</span></p>
          </div>
          <div className={`${cls.card} border ${isLight ? 'border-cyan-300' : 'border-cyan-500/20'} rounded-xl p-4`}>
            <p className="text-[11px] text-cyan-600 uppercase tracking-wider mb-1">Distance (d)</p>
            <p className="text-2xl font-bold font-mono text-teal-600">{display.d.toFixed(1)}<span className={`text-sm ${cls.mutedText} ml-1`}>m</span></p>
          </div>
          <div className={`${cls.card} border ${isLight ? 'border-amber-300' : 'border-amber-500/20'} rounded-xl p-4`}>
            <p className="text-[11px] text-amber-600 uppercase tracking-wider mb-1">Velocity (v)</p>
            <p className="text-2xl font-bold font-mono text-amber-600">{display.v.toFixed(2)}<span className={`text-sm ${cls.mutedText} ml-1`}>m/s</span></p>
          </div>
        </div>

        {/* Track */}
        <div className={`${cls.card} border rounded-2xl p-4 mb-6`}>
          <canvas ref={trackRef} width={TRACK_W} height={TRACK_H} className="w-full h-auto rounded-lg" data-testid="track-canvas" />
        </div>

        {/* Graphs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className={`${cls.card} border rounded-2xl p-4`}>
            <canvas ref={dtGraphRef} width={GRAPH_W} height={GRAPH_H} className="w-full h-auto rounded-lg" data-testid="dt-graph" />
          </div>
          <div className={`${cls.card} border rounded-2xl p-4`}>
            <canvas ref={vtGraphRef} width={GRAPH_W} height={GRAPH_H} className="w-full h-auto rounded-lg" data-testid="vt-graph" />
          </div>
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className={`${cls.card} border rounded-2xl p-5`}>
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="w-4 h-4 text-cyan-500" />
              <p className="text-sm font-semibold">Acceleration (a)</p>
              <span className="ml-auto text-lg font-bold text-cyan-600">{acceleration.toFixed(1)} m/s²</span>
            </div>
            <Slider
              value={[acceleration]}
              onValueChange={(v) => setAcceleration(v[0])}
              min={-5}
              max={5}
              step={0.1}
              data-testid="accel-slider"
              className="my-3"
            />
            <div className={`flex justify-between text-[10px] ${cls.label} uppercase tracking-wider`}>
              <span>-5 (brake)</span>
              <span>0 (uniform)</span>
              <span>+5 (boost)</span>
            </div>

            <div className={`flex items-center justify-between gap-3 mt-5 pt-5 border-t ${isLight ? 'border-gray-200' : 'border-white/5'}`}>
              <Badge className={`${isLight ? 'bg-gray-100 text-gray-700 border-gray-200' : 'bg-white/5 text-gray-300 border-white/10'} text-[11px]`}>{motionType}</Badge>
              <div className="flex gap-2">
                <Button onClick={() => setRunning(!running)} variant="outline" className={cls.btnOutline} data-testid="play-pause">
                  {running ? <><Pause className="w-4 h-4 mr-2" />Pause</> : <><Play className="w-4 h-4 mr-2" />Play</>}
                </Button>
                <Button onClick={reset} variant="outline" className={cls.btnOutline} data-testid="reset">
                  <RotateCcw className="w-4 h-4 mr-2" />Reset
                </Button>
              </div>
            </div>
          </div>

          {/* Try this */}
          <div className={`${cls.card} border ${isLight ? 'border-cyan-300' : 'border-cyan-500/30'} rounded-2xl p-5`}>
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-cyan-500" />
              <p className="text-sm font-semibold text-cyan-600">Try this</p>
            </div>
            <ul className={`text-xs ${cls.softText} space-y-2 list-decimal list-inside leading-relaxed`}>
              <li>Set <span className="text-cyan-600">a = 0</span> — velocity stays constant (uniform motion). D-T graph is a straight line.</li>
              <li>Set <span className="text-cyan-600">a = +2</span> — velocity rises linearly. D-T graph curves upward (parabola).</li>
              <li>Set <span className="text-amber-600">a = -3</span> — car decelerates and stops.</li>
            </ul>
          </div>
        </div>

        {/* Concept note */}
        <div className={`mt-6 ${isLight ? 'bg-cyan-50 border-cyan-200' : 'bg-gradient-to-r from-cyan-500/[0.04] to-blue-500/[0.04] border-cyan-500/20'} border rounded-2xl p-5`}>
          <p className="text-[11px] text-cyan-600 uppercase tracking-widest font-medium mb-2">Equations of Motion (NCERT)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm font-mono">
            {[
              { lbl: 'First', eq: 'v = u + at' },
              { lbl: 'Second', eq: 's = ut + ½at²' },
              { lbl: 'Third', eq: 'v² = u² + 2as' },
            ].map(e => (
              <div key={e.lbl} className={`${isLight ? 'bg-white border-gray-200' : 'bg-white/[0.02] border-white/5'} rounded-lg p-3 border`}>
                <p className={`${cls.label} text-[10px] mb-1`}>{e.lbl}</p>
                <p>{e.eq}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotionSimulation;
